import { formatRelativeTime } from '@src/utility/relativeTime';

import { previousDirectionRecordForLine } from './directionPrefill';

import type {
  DubDirection,
  DubDirectionRecordDTO,
  DubManifestDTO,
} from '@src/repositories/DubbingRepository';

/**
 * PLAT-12024 — what a landed re-dub says about itself. Pure: no React, no IO.
 *
 * After a section re-dub lands, the only sign used to be a toast. Now the
 * re-dubbed words light up in the transcript, and hovering them says what was
 * asked for and when, read from the stored dub rather than from whatever the
 * popover remembered — the manifest is the record of what was actually made.
 */

/** A re-dub that has just landed on these lines. */
export interface RedubLanded {
  /** The lines the run re-recorded (the engine's `segmentIds`). */
  segmentIds: readonly string[];
  /** When it landed, ISO. The pulse is keyed on it: a new landing pulses again. */
  at: string;
  /**
   * Bumped by the toast's action to bring the passage back into view and play
   * it, without a new landing.
   */
  revealToken?: number;
}

/** The direction in the words a hover reads. */
export function directionPhrase(direction: DubDirection | undefined): string {
  if (!direction) return '';
  switch (direction.kind) {
    case 'free-form':
      return `“${direction.note.trim()}”`;
    case 'config': {
      const parts = Object.entries(direction.settings).map(
        ([name, value]) => `${name.replace(/_/g, ' ')} ${String(value)}`,
      );
      return parts.join(', ');
    }
    case 'tags':
    case 'tags-by-line':
      return 'delivery markers';
  }
}

/**
 * The hover line for one re-dubbed line: when it was re-dubbed, with what
 * direction, and whether the voice model could honour all of it.
 *
 * `null` when the dub has no record of the line, which is the only case with
 * nothing honest to say.
 */
export function landedHover(
  manifest: DubManifestDTO | null | undefined,
  segmentId: string,
  now: Date = new Date(),
): string | null {
  const segment = (manifest?.segments ?? []).find(
    s => s.segmentId === segmentId,
  );
  if (!segment) return null;
  const when = segment.producedAt
    ? formatRelativeTime(segment.producedAt)
    : 'just now';
  const record = previousDirectionRecordForLine(manifest, segmentId);
  const asked = record ? directionPhrase(record.direction) : '';
  const line = asked ? `Re-dubbed ${when} with ${asked}` : `Re-dubbed ${when}`;
  const caveat = partlyHonoured(record)
    ? '; some of the direction couldn’t be applied by this voice'
    : '';
  // `now` is unused by the formatter but is part of the contract so a test can
  // pin the wording without freezing the clock.
  void now;
  return `${line}${caveat}.`;
}

/**
 * PLAT-11380's marker: a direction the model took in part. Read off the
 * recorded interpretation — anything the model reported as unexpressed means
 * the take is not quite what was asked for, and the hover says so rather than
 * letting a pulse read as "done exactly as asked".
 */
function partlyHonoured(record: DubDirectionRecordDTO | null): boolean {
  if (!record) return false;
  return record.interpretation.some(i => i.unexpressed.length > 0);
}
